import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Search, Unplug, CreditCard, RotateCcw, Loader2 } from 'lucide-react';
import {
  fetchUnsynchronizedPolicies,
  retryCoreSubmission,
  startUnsynchronizedCoreRetry,
} from '../services/adminAnalyticsApi';
import { publishCoreRetryJob, subscribeCoreRetry } from '../services/coreRetryBus';
import Snackbar from './Snackbar';
import '../styles/analytics-theme.css';

const PAGE_SIZES = [10, 25, 50, 100];

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-KE', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function CoverTypeBadge({ value }) {
  const n = String(value || '').toUpperCase();
  if (n === 'TPO') return <span className="ab-badge sky">{n}</span>;
  if (n === 'TOR') return <span className="ab-badge violet">{n}</span>;
  return <span className="ab-badge muted">{n || '—'}</span>;
}

function CoreStatusBadge({ value }) {
  const raw = String(value || '').trim() || '—';
  const lower = raw.toLowerCase();
  if (lower === 'submitted') return <span className="ab-badge success">{raw}</span>;
  if (lower === 'skipped') return <span className="ab-badge muted">{raw}</span>;
  if (lower === 'pending_review') return <span className="ab-badge violet">pending review</span>;
  if (lower === 'submitting') return <span className="ab-badge sky">{raw}</span>;
  return <span className="ab-badge warning">{raw}</span>;
}

function PaymentBadge({ value }) {
  if (!value) return <span style={{ color: 'var(--ab-muted-fg)', fontSize: '0.75rem' }}>—</span>;
  const ok = ['completed', 'success', 'paid'].includes(String(value).toLowerCase());
  return <span className={`ab-badge ${ok ? 'success' : 'muted'}`}>{value}</span>;
}

function outcomeToast(item) {
  if (!item) return null;
  const who = item.policyNumber || item.clientId || 'Policy';
  const detail = String(item.message || '').trim();
  const text = detail ? `${who}: ${detail}` : who;
  if (item.status === 'submitted') return { type: 'success', duration: 4500, message: text };
  if (item.status === 'pending_review' || item.status === 'skipped') return { type: 'warning', duration: 12000, message: text };
  if (item.status === 'failed') return { type: 'error', duration: 12000, message: text };
  if (item.status === 'noop') return { type: 'info', duration: 6000, message: text };
  return null;
}

function ToastStack({ toasts, onDismiss }) {
  if (!toasts.length) return null;
  return (
    <div className="ab-toast-stack" aria-live="polite">
      {toasts.map((toast) => (
        <Snackbar
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => onDismiss(toast.id)}
        />
      ))}
    </div>
  );
}

function AccentStatCard({ title, value, subtitle, icon: Icon, accent }) {
  return (
    <div className={`ab-accent-card ${accent}`}>
      <div className="ab-accent-card-inner">
        <div className={`ab-accent-icon ${accent}`}><Icon size={24} /></div>
        <div>
          <p className="ab-stat-label">{title}</p>
          <p className="ab-stat-value">{value}</p>
          {subtitle && <p className="ab-stat-subtitle">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
}

const UnsynchronizedPolicies = ({ onShowNotification }) => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [data, setData] = useState({ rows: [], total: 0, stats: {} });
  const [retryingId, setRetryingId] = useState(null);
  const [retrySnap, setRetrySnap] = useState({ jobId: null, job: null });
  const loadDataRef = useRef(null);
  const [toasts, setToasts] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const pushToast = useCallback((message, type = 'info', duration = 5000) => {
    if (!message) return;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setToasts((prev) => {
      const next = [...prev, { id, message, type, duration }];
      while (next.length > 4) {
        const drop = next.findIndex((item) => item.type === 'success' || item.type === 'info');
        next.splice(drop === -1 ? 0 : drop, 1);
      }
      return next;
    });
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const loadData = useCallback(async () => {
    setFetching(true);
    try {
      const result = await fetchUnsynchronizedPolicies({ search, page: currentPage, pageSize });
      setData(result);
    } catch (error) {
      if (onShowNotification) onShowNotification(error.message, 'error');
    } finally {
      setLoading(false);
      setFetching(false);
    }
  }, [search, currentPage, pageSize, onShowNotification]);

  loadDataRef.current = loadData;

  useEffect(() => {
    const timer = setTimeout(() => { setSearch(searchInput); setCurrentPage(1); }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => subscribeCoreRetry(setRetrySnap), []);

  const bulkRunning = retrySnap.job?.status === 'running' || (!!retrySnap.jobId && !retrySnap.job);

  useEffect(() => {
    if (!bulkRunning || !retrySnap.job?.processed) return undefined;
    if (loadDataRef.current) loadDataRef.current();
    return undefined;
  }, [bulkRunning, retrySnap.job?.processed]);

  const handleRetryCore = async (row) => {
    if (!row.client_id || retryingId || bulkRunning) return;
    setRetryingId(row.client_id);
    try {
      const result = await retryCoreSubmission({
        clientId: row.client_id,
        referenceNumber: row.reference_number || null,
      });
      const toast = outcomeToast({
        clientId: row.client_id,
        policyNumber: row.policy_number,
        status: result.status || (result.success ? 'submitted' : 'failed'),
        message: result.message || 'Core submission completed',
      });
      if (toast) pushToast(toast.message, toast.type, toast.duration);
      else pushToast(result.message || 'Core submission completed', result.success ? 'success' : 'error', result.success ? 5000 : 12000);
      await loadData();
    } catch (error) {
      pushToast(error.message, 'error', 12000);
    } finally {
      setRetryingId(null);
    }
  };

  const handleRetryAll = () => {
    if (bulkRunning || retryingId || !(data.total > 0)) return;
    setConfirmOpen(true);
  };

  const startRetryAll = async () => {
    setConfirmOpen(false);
    try {
      const started = await startUnsynchronizedCoreRetry({ search });
      publishCoreRetryJob({ ...started, status: started.status || 'running' });
      if (onShowNotification) {
        onShowNotification(
          started.alreadyRunning
            ? 'A core send is already running in the background.'
            : 'Core send started. You can leave this page. It keeps running.',
          started.alreadyRunning ? 'warning' : 'info',
          6000
        );
      }
    } catch (error) {
      pushToast(error.message, 'error', 12000);
    }
  };

  useEffect(() => {
    if (!confirmOpen) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setConfirmOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirmOpen]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / pageSize));
  const current = Math.min(currentPage, totalPages);
  const start = data.total === 0 ? 0 : (current - 1) * pageSize + 1;
  const end = Math.min(current * pageSize, data.total || 0);

  if (loading) {
    return (
      <>
        <ToastStack toasts={toasts} onDismiss={dismissToast} />
        <div className="ab-page"><div className="ab-empty">Loading unsynchronized policies…</div></div>
      </>
    );
  }

  return (
    <>
    <ToastStack toasts={toasts} onDismiss={dismissToast} />
    {confirmOpen && (
      <div className="ab-dialog-backdrop" onClick={() => setConfirmOpen(false)} role="presentation">
        <div
          className="ab-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="unsync-retry-title"
          onClick={(event) => event.stopPropagation()}
        >
          <p className="ab-dialog-kicker">Send to core</p>
          <h2 id="unsync-retry-title" className="ab-dialog-title">
            Send {(data.total || 0).toLocaleString()} unsynchronized {Number(data.total) === 1 ? 'policy' : 'policies'}?
          </h2>
          <p className="ab-dialog-copy">
            {search.trim()
              ? 'This sends only the policies matching the current search. '
              : 'This sends every outstanding policy in the list. '}
            Each certificate is posted once, and the send keeps running if you open another page. A missing engine number is filled from DMVIC, a policy already in core is marked submitted, and each status updates when you come back to this list.
          </p>
          <div className="ab-dialog-actions">
            <button type="button" className="ab-btn" onClick={() => setConfirmOpen(false)}>Cancel</button>
            <button type="button" className="ab-btn primary" onClick={startRetryAll}>Send to core</button>
          </div>
        </div>
      </div>
    )}
    <div className="ab-page">
      <div className="ab-page-header">
        <div className="ab-header-with-icon">
          <div className="ab-page-icon-wrap" style={{ background: '#fef3c7', color: '#d97706' }}><Unplug size={20} /></div>
          <div>
            <h1 className="ab-page-title">Unsynchronized Policies</h1>
            <p className="ab-page-subtitle" style={{ maxWidth: 640 }}>
              Issued DMVIC certificates where core submission is not marked submitted or skipped. Use this list to reconcile billing core.
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem' }}>
          <button
            type="button"
            className="ab-btn primary"
            disabled={bulkRunning || !!retryingId || !(data.total > 0)}
            onClick={handleRetryAll}
            title="Send every outstanding policy in this list to core and update each status"
            style={{ whiteSpace: 'nowrap' }}
          >
            {bulkRunning ? <Loader2 size={16} className="ab-spin" /> : <RotateCcw size={16} />}
            {bulkRunning
              ? `Sending ${retrySnap.job?.processed || 0} of ${retrySnap.job?.total || data.total || 0}`
              : 'Retry all'}
          </button>
          {bulkRunning && (
            <span className="ab-refresh-badge">
              {retrySnap.job?.submitted || 0} submitted · {retrySnap.job?.failed || 0} failed
              {retrySnap.job?.pendingReview ? ` · ${retrySnap.job.pendingReview} for review` : ''}
            </span>
          )}
          {fetching && !bulkRunning && <span className="ab-refresh-badge">Refreshing…</span>}
        </div>
      </div>

      <div className="ab-stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', maxWidth: 720 }}>
        <AccentStatCard title="Outstanding" value={(data.total || 0).toLocaleString()} subtitle="Rows matching filters below" icon={Unplug} accent="amber" />
        <AccentStatCard title="With successful payment" value={Number(data.stats?.with_payment_record || 0).toLocaleString()} subtitle="Latest payment_status completed / success / paid" icon={CreditCard} accent="emerald" />
      </div>

      <div className="ab-card" style={{ marginBottom: '1.5rem' }}>
        <div className="ab-card-body">
          <label className="ab-stat-label">Search</label>
          <div className="ab-search-wrap" style={{ marginTop: '0.375rem' }}>
            <Search className="ab-search-icon" size={16} />
            <input
              className="ab-search-input"
              placeholder="Client ID, policy, insured, certificate, core ref, error, payment ref…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="ab-card">
        <div className="ab-card-header"><h3 className="ab-card-title">Policies</h3></div>
        <div className="ab-card-body-flush">
          <div className="ab-table-wrap">
            <table className="ab-table" style={{ minWidth: 1380 }}>
              <thead>
                <tr>
                  <th>Client ID</th>
                  <th>Policy</th>
                  <th>Insured</th>
                  <th>Class</th>
                  <th>Cover</th>
                  <th>Certificate #</th>
                  <th>Issued</th>
                  <th>Core status</th>
                  <th>Core ref</th>
                  <th>Core submitted</th>
                  <th>Core error</th>
                  <th>Pay ref</th>
                  <th>Payment</th>
                  <th style={{ textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {(data.rows || []).map((row) => {
                  const isRetrying = retryingId === row.client_id;
                  const status = String(row.core_submission_status || '').toLowerCase();
                  const canRetry = status !== 'submitted' && status !== 'skipped';
                  return (
                    <tr key={row.client_id}>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{row.client_id ?? '—'}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{row.policy_number ?? '—'}</td>
                      <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.insured_name}>{row.insured_name ?? '—'}</td>
                      <td style={{ fontSize: '0.75rem', color: 'var(--ab-muted-fg)' }}>{row.vehicle_class ?? '—'}</td>
                      <td><CoverTypeBadge value={row.cover_type} /></td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{row.dmvic_certificate_number ?? '—'}</td>
                      <td style={{ fontSize: '0.75rem', color: 'var(--ab-muted-fg)' }}>{formatDate(row.dmvic_issued_at)}</td>
                      <td><CoreStatusBadge value={row.core_submission_status} /></td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.75rem', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.core_reference_number}>{row.core_reference_number ?? '—'}</td>
                      <td style={{ fontSize: '0.75rem', color: 'var(--ab-muted-fg)' }}>{formatDateTime(row.core_submitted_at)}</td>
                      <td style={{ fontSize: '0.75rem', color: 'var(--ab-destructive)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.core_submission_error}>{row.core_submission_error ?? '—'}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{row.reference_number ?? '—'}</td>
                      <td><PaymentBadge value={row.payment_status} /></td>
                      <td style={{ textAlign: 'right' }}>
                        {canRetry ? (
                          <button
                            type="button"
                            className="ab-btn sm primary"
                            disabled={isRetrying || bulkRunning}
                            onClick={() => handleRetryCore(row)}
                            title="Retry core billing submission"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', whiteSpace: 'nowrap' }}
                          >
                            {isRetrying ? <Loader2 size={14} className="ab-spin" /> : <RotateCcw size={14} />}
                            {isRetrying ? 'Submitting…' : 'Retry Core'}
                          </button>
                        ) : (
                          <span style={{ fontSize: '0.75rem', color: 'var(--ab-muted-fg)' }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!data.rows?.length && (
                  <tr><td colSpan={14} className="ab-empty">No unsynchronized policies match the current search.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="ab-pagination">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.75rem', color: 'var(--ab-muted-fg)' }}>Rows per page</span>
              <select className="ab-select" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>
                {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <p style={{ fontSize: '0.75rem', color: 'var(--ab-muted-fg)', margin: 0 }}>Showing {start}-{end} of {(data.total || 0).toLocaleString()} policies</p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" className="ab-btn sm" disabled={current === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>Prev</button>
              <span style={{ fontSize: '0.75rem', color: 'var(--ab-muted-fg)', minWidth: 80, textAlign: 'center', lineHeight: '2rem' }}>Page {current} of {totalPages}</span>
              <button type="button" className="ab-btn sm" disabled={current === totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>Next</button>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default UnsynchronizedPolicies;
